import type { ComponentProps, ReactNode } from 'react'
import { cn } from '@/lib/utils'

/* Card — the container everything on a web screen sits in. The header's
   left-to-right gradient and the 30%-alpha primary border are both measured
   from the client's application. */

export function Card({ className, ...props }: ComponentProps<'section'>) {
  return (
    <section
      className={cn(
        'flex flex-col overflow-hidden rounded-hk-card bg-card shadow-card',
        'border border-[color-mix(in_oklab,var(--hk-primary)_30%,transparent)] dark:border-border',
        className,
      )}
      {...props}
    />
  )
}

export function CardHeader(
  { title, subtitle, icon, actions, className, ...props }:
  // `title` is OMITTED from the host element's props before ours is added. <header>
  // carries an HTML `title` attribute typed as string, and without the omit the two
  // intersect to `string & ReactNode` — so passing a <span> as the heading fails to
  // compile with a message about ReactPortal that says nothing about the cause.
  Omit<ComponentProps<'header'>, 'title'> & {
    title: ReactNode; subtitle?: ReactNode; icon?: ReactNode; actions?: ReactNode
  },
) {
  return (
    <header
      className={cn(
        'flex items-center justify-between gap-4 px-6 py-4 border-b border-border',
        'bg-linear-to-r from-[#f0f9ff] to-[#eff6ff] dark:from-[#16283d] dark:to-[#152238]',
        className,
      )}
      {...props}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2.5 text-[15px] font-semibold text-foreground">
          {icon}
          <span className="truncate">{title}</span>
        </div>
        {subtitle && <p className="mt-0.5 text-xs font-normal text-muted-fg">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  )
}

export function CardBody({ className, flush, ...props }: ComponentProps<'div'> & { flush?: boolean }) {
  return <div className={cn(flush ? 'p-0' : 'px-6 py-5', className)} {...props} />
}
